import { useState } from 'react';
import { getAllIndicators, getIndicator } from '../indicators/registry';

const LINE_STYLES = [
  { value: 1, label: 'Solid' },
  { value: 2, label: 'Dashed' },
  { value: 3, label: 'Dotted' },
];

const SERIES_LABELS = {
  line: 'Line',
  upper: 'Upper', middle: 'Middle', lower: 'Lower',
  macdLine: 'MACD', signalLine: 'Signal', histogram: 'Histogram',
  k: '%K', d: '%D',
  adx: 'ADX', plusDI: '+DI', minusDI: '-DI', adxr: 'ADXR',
  tenkanSen: 'Tenkan', kijunSen: 'Kijun', senkouA: 'Senkou A', senkouB: 'Senkou B', chikouSpan: 'Chikou',
  pivot: 'Pivot', r1: 'R1', r2: 'R2', r3: 'R3', s1: 'S1', s2: 'S2', s3: 'S3',
  up: 'Up', down: 'Down', oscillator: 'Oscillator',
  bull: 'Bull', bear: 'Bear',
  kst: 'KST', signal: 'Signal',
};

export default function IndicatorSelector({ activeIndicators, onAdd, onRemove, onUpdate, editingId: externalEditingId, onEditingChange }) {
  const [showMenu, setShowMenu] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [internalEditingId, setInternalEditingId] = useState(null);
  const editingId = externalEditingId !== undefined ? externalEditingId : internalEditingId;
  const setEditingId = onEditingChange || setInternalEditingId;

  const allIndicators = getAllIndicators();

  const handleAdd = (indicatorId) => {
    const indicator = getIndicator(indicatorId);
    if (!indicator) return;

    const params = {};
    for (const p of indicator.params) {
      params[p.key] = p.default;
    }
    // Initialize colors from registry defaults
    indicator.colors.forEach((c, i) => {
      params[`color_${i}`] = c;
    });
    // Default line width
    params.lineWidth = 2;

    onAdd({
      indicatorId,
      instanceId: `${indicatorId}_${Date.now()}`,
      params,
    });
    setShowMenu(false);
  };

  const handleParamChange = (instanceId, key, value) => {
    if (key.startsWith('color_') || key === 'lineWidth' || key === 'lineStyle') {
      onUpdate(instanceId, { [key]: value });
    } else if (key.startsWith('hide_') || typeof value === 'boolean') {
      onUpdate(instanceId, { [key]: value });
    } else {
      // Check if the param is text type
      const ai = activeIndicators.find(a => a.instanceId === instanceId);
      const ind = ai ? getIndicator(ai.indicatorId) : null;
      const paramDef = ind?.params?.find(p => p.key === key);
      if (paramDef?.type === 'text' || paramDef?.type === 'select') {
        onUpdate(instanceId, { [key]: value });
      } else {
        onUpdate(instanceId, { [key]: parseFloat(value) || 0 });
      }
    }
  };

  return (
    <div className="indicator-selector">
      <div className="indicator-selector-header">
        <button className="replay-btn" onClick={() => setShowMenu(!showMenu)}>
          + Indicators
        </button>
        {activeIndicators.length > 0 && (
          <button className="indicator-collapse-btn" onClick={() => setCollapsed(v => !v)} title={collapsed ? 'Show indicators' : 'Hide indicators'}>
            <span className="indicator-collapse-count">{activeIndicators.length}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.15s' }}>
              <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        {!collapsed && (
          <div className="indicator-pills">
            {activeIndicators.map((ai) => {
              const ind = getIndicator(ai.indicatorId);
              if (!ind) return null;
              const dotColor = ai.params.color_0 || ind.colors[0] || '#6366f1';
              return (
                <div key={ai.instanceId} className="indicator-pill">
                  <span
                    className="indicator-pill-dot"
                    style={{ background: dotColor }}
                  />
                  <span
                    className="indicator-pill-name"
                    onClick={() => setEditingId(editingId === ai.instanceId ? null : ai.instanceId)}
                  >
                    {ind.name}
                    {ind.params.length > 0 && (
                      <span className="indicator-pill-params">
                        ({ind.params.map((p) => ai.params[p.key]).join(', ')})
                      </span>
                    )}
                  </span>
                  <button className="indicator-pill-remove" onClick={() => onRemove(ai.instanceId)}>
                    &times;
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showMenu && (
        <div className="indicator-menu">
          <div className="indicator-menu-section">
            <h4>Overlay</h4>
            {allIndicators
              .filter((i) => i.displayType === 'overlay')
              .map((i) => (
                <button key={i.id} className="indicator-menu-item" onClick={() => handleAdd(i.id)}>
                  {i.name} <span className="indicator-menu-desc">{i.fullName}</span>
                </button>
              ))}
          </div>
          <div className="indicator-menu-section">
            <h4>Oscillators</h4>
            {allIndicators
              .filter((i) => i.displayType === 'panel')
              .map((i) => (
                <button key={i.id} className="indicator-menu-item" onClick={() => handleAdd(i.id)}>
                  {i.name} <span className="indicator-menu-desc">{i.fullName}</span>
                </button>
              ))}
          </div>
        </div>
      )}

      {editingId && (() => {
        const ai = activeIndicators.find((a) => a.instanceId === editingId);
        if (!ai) return null;
        const ind = getIndicator(ai.indicatorId);
        if (!ind) return null;
        return (
          <div className="indicator-params-editor">
            <strong>{ind.fullName}</strong>

            {/* Params */}
            {ind.params.map((p) => (
              <div key={p.key} className="indicator-param-row">
                <label>{p.label}</label>
                {p.type === 'select' ? (
                  <select
                    value={ai.params[p.key] || p.default}
                    onChange={(e) => handleParamChange(ai.instanceId, p.key, e.target.value)}
                    className="indicator-select-input"
                  >
                    {p.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : p.type === 'text' ? (
                  <input
                    type="text"
                    value={ai.params[p.key] || ''}
                    onChange={(e) => handleParamChange(ai.instanceId, p.key, e.target.value)}
                    style={{ width: '100%', fontSize: '0.75rem' }}
                    placeholder={p.default}
                  />
                ) : (
                  <input
                    type="number"
                    value={ai.params[p.key]}
                    min={p.min} max={p.max} step={p.step || 1}
                    onChange={(e) => handleParamChange(ai.instanceId, p.key, e.target.value)}
                  />
                )}
              </div>
            ))}

            {/* Color pickers + visibility toggles per series */}
            <div className="indicator-param-divider" />
            {ind.series.map((seriesKey, i) => {
              // Hide internal cloud series from UI
              if (seriesKey === 'cloudUp' || seriesKey === 'cloudDown') return null;
              const isHidden = ai.params[`hide_${i}`] || false;
              const label = SERIES_LABELS[seriesKey] || (ind.series.length === 1 ? 'Color' : seriesKey);
              return (
                <div key={seriesKey} className="indicator-param-row">
                  <button
                    className={`indicator-vis-btn ${isHidden ? 'off' : ''}`}
                    onClick={() => handleParamChange(ai.instanceId, `hide_${i}`, !isHidden)}
                    title={isHidden ? 'Show' : 'Hide'}
                  >
                    {isHidden ? '○' : '●'}
                  </button>
                  <label style={{ opacity: isHidden ? 0.4 : 1 }}>{label}</label>
                  <input
                    type="color"
                    value={ai.params[`color_${i}`] || ind.colors[i] || '#6366f1'}
                    onChange={(e) => handleParamChange(ai.instanceId, `color_${i}`, e.target.value)}
                    className="indicator-color-input"
                    disabled={isHidden}
                  />
                </div>
              );
            })}

            {/* Line width */}
            <div className="indicator-param-row">
              <label>Width</label>
              <input
                type="range" min="1" max="5" step="1"
                value={ai.params.lineWidth || 2}
                onChange={(e) => handleParamChange(ai.instanceId, 'lineWidth', parseInt(e.target.value))}
                className="indicator-range-input"
              />
              <span className="indicator-range-value">{ai.params.lineWidth || 2}px</span>
            </div>

            <button className="replay-btn" onClick={() => setEditingId(null)}>Done</button>
          </div>
        );
      })()}
    </div>
  );
}
